import { useEffect, useState, type FormEvent } from "react";
import { useLocale } from "../../shared/i18n/LocaleContext";
import type { TranslationKey } from "../../shared/i18n/translations";
import {
  getTraceability,
  saveTraceability,
  type TraceabilitySnapshot,
  type Row,
} from "./traceabilityApi";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { AppModal } from "../../shared/components/AppModal";
export type TraceabilitySection =
  | "policies"
  | "attributes"
  | "certificates"
  | "requirements"
  | "genealogy"
  | "expiry";
const k = (x: string) => x as TranslationKey;
export function TraceabilityPage({
  section,
}: {
  section: TraceabilitySection;
}) {
  const { t } = useLocale();
  const [data, setData] = useState<TraceabilitySnapshot | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const load = () => {
    void getTraceability("")
      .then(setData)
      .catch((e) =>
        setError(
          t(k(e instanceof Error ? e.message : "traceability.loadError")),
        ),
      );
  };
  useEffect(load, [section]);
  const records = (
    data
      ? section === "policies"
        ? data.policies
        : section === "attributes"
          ? data.attributes
          : section === "certificates"
            ? data.certificates
            : section === "requirements"
              ? data.requirements
              : section === "expiry"
                ? data.expiryAlerts
                : data.movements
      : []
  ) as Row[];
  const list = useClientRecordList(records, `traceability:${section}`);
  if (!data)
    return (
      <main className="page-content">
        {error ? (
          <p className="error-banner" role="alert">
            {error}
          </p>
        ) : (
          <p>{t("common.loading")}</p>
        )}
      </main>
    );
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t(k("traceability.eyebrow"))}</span>
          <h1>{t(k(`traceability.${section}`))}</h1>
          <p>{t(k(`traceability.${section}Description`))}</p>
        </div>
      </header>
      {editing && (
        <TraceabilityForm
          section={section}
          data={data}
          done={() => {
            setEditing(false);
            load();
          }}
        />
      )}
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k(`traceability.${section}`))}</h2>
          {!["genealogy", "expiry"].includes(section) && (
            <button className="primary-button" onClick={() => setEditing(true)}>
              + {t("administration.add")}
            </button>
          )}
        </div>
        <ClientRecordListControls list={list} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead>
              <tr>
                <SortableHeader
                  label={t("aplPanel.code")}
                  active={list.sort === "code"}
                  direction={list.direction}
                  onClick={() => list.changeSort("code")}
                />
                <SortableHeader
                  label={t("aplPanel.name")}
                  active={list.sort === "name"}
                  direction={list.direction}
                  onClick={() => list.changeSort("name")}
                />
                <SortableHeader
                  label={t("planning.status")}
                  active={list.sort === "status"}
                  direction={list.direction}
                  onClick={() => list.changeSort("status")}
                />
                <th>{t(k("traceability.detail"))}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((r, i) => (
                <TraceRow
                  key={r.id ?? i}
                  row={r}
                  data={data}
                  section={section}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!list.rows.length && (
          <p className="empty-state">{t(k("traceability.empty"))}</p>
        )}
      </section>
    </main>
  );
}
function TraceRow({
  row,
  data,
  section,
}: {
  row: Row;
  data: TraceabilitySnapshot;
  section: TraceabilitySection;
}) {
  const code = String(
    row.number ?? row.code ?? row.lotNumber ?? row.serialNumber ?? row.id,
  );
  const movement =
    section === "genealogy"
      ? data.links.filter(
          (x) => x.parentMovementId === row.id || x.childMovementId === row.id,
        ).length
      : null;
  return (
    <tr>
      <td>{code}</td>
      <td>
        {String(
          row.type ??
            row.value ??
            row.method ??
            row.severity ??
            row.reference ??
            "—",
        )}
      </td>
      <td>{String(row.status ?? row.isActive ?? "—")}</td>
      <td>
        {movement ??
          String(
            row.daysRemaining ??
              row.requiredRevision ??
              row.preferredLotNumber ??
              "—",
          )}
      </td>
    </tr>
  );
}
function TraceabilityForm({
  section,
  data,
  done,
}: {
  section: TraceabilitySection;
  data: TraceabilitySnapshot;
  done: () => void;
}) {
  const { t } = useLocale();
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [code, setCode] = useState("");
  const [value, setValue] = useState("");
  const [extraCode, setExtraCode] = useState("");
  const [extraValue, setExtraValue] = useState("");
  const [days, setDays] = useState(0);
  const [method, setMethod] = useState("Fefo");
  const [substitution, setSubstitution] = useState(false);
  const [serialMode, setSerialMode] = useState(false);
  const [issuer, setIssuer] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [error, setError] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      if (section === "policies")
        await saveTraceability("policies", null, {
          articleId: primary,
          warehouseId: secondary || null,
          method,
          preferredLotNumber: value,
          minimumRemainingShelfLifeDays: days,
          allowSubstitution: substitution,
          isActive: true,
        });
      if (section === "attributes")
        await saveTraceability("attributes", null, {
          inventoryLotSerialId: primary,
          code,
          value,
          unit: extraValue,
        });
      if (section === "certificates")
        await saveTraceability("certificates", null, {
          articleId: primary,
          inventoryLotSerialId: serialMode ? null : secondary || null,
          inventorySerialNumberId: serialMode ? secondary || null : null,
          number: code,
          type: value,
          issuer,
          issuedOn: issuedOn || null,
          validUntil: validUntil || null,
          externalReference: extraValue,
          contentHash: "",
          status: "Valid",
        });
      if (section === "requirements")
        await saveTraceability("requirements", null, {
          bomLineId: primary,
          requiredRevision: code,
          requiredCertificateType: value,
          requiredAttributeCode: extraCode,
          requiredAttributeValue: extraValue,
          minimumRemainingShelfLifeDays: days,
        });
      done();
    } catch (e) {
      setError(t(k(e instanceof Error ? e.message : "traceability.saveError")));
    }
  };
  const refs =
    section === "attributes"
      ? data.lots
      : section === "requirements"
        ? data.bomLines
        : data.articles;
  return (
    <AppModal
      title={t(k(`traceability.${section}`))}
      closeLabel={t("recordNavigation.backToList")}
      onClose={done}
      saveForm
      wide
      feedback={
        error ? (
          <p className="error-banner" role="alert">
            {error}
          </p>
        ) : null
      }
    >
      <form className="administration-form record-form" onSubmit={submit}>
        <div className="compact-form-grid">
          <label>
            <span>{t(k("traceability.reference"))}</span>
            <select
              required
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
            >
              <option value="">—</option>
              {refs.map((x) => (
                <option value={x.id} key={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
            </select>
          </label>
          {section === "policies" && (
            <>
              <label>
                <span>{t(k("traceability.warehouse"))}</span>
                <select
                  value={secondary}
                  onChange={(e) => setSecondary(e.target.value)}
                >
                  <option value="">{t(k("traceability.allWarehouses"))}</option>
                  {data.warehouses.map((x) => (
                    <option value={x.id} key={x.id}>
                      {x.code}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{t(k("traceability.method"))}</span>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  {["Fifo", "Fefo", "PreferredLot"].map((x) => (
                    <option key={x} value={x}>
                      {t(k(`traceability.method.${x}`))}
                    </option>
                  ))}
                </select>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={substitution}
                  onChange={(e) => setSubstitution(e.target.checked)}
                />
                <span>{t(k("traceability.allowSubstitution"))}</span>
              </label>
            </>
          )}
          {section === "certificates" && (
            <>
              <label>
                <span>{t(k("traceability.identityType"))}</span>
                <select
                  value={serialMode ? "serial" : "lot"}
                  onChange={(e) => {
                    setSerialMode(e.target.value === "serial");
                    setSecondary("");
                  }}
                >
                  <option value="lot">{t(k("traceability.lot"))}</option>
                  <option value="serial">{t(k("traceability.serial"))}</option>
                </select>
              </label>
              <label>
                <span>
                  {serialMode
                    ? t(k("traceability.serial"))
                    : t(k("traceability.lot"))}
                </span>
                <select
                  required
                  value={secondary}
                  onChange={(e) => setSecondary(e.target.value)}
                >
                  <option value="">—</option>
                  {(serialMode ? data.serialNumbers : data.lots).map((x) => (
                    <option value={x.id} key={x.id}>
                      {x.code}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label>
            <span>{t(k("traceability.code"))}</span>
            <input
              required={section !== "policies"}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <label>
            <span>{t(k("traceability.value"))}</span>
            <input
              required={section === "attributes" || section === "certificates"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
          {section === "certificates" && (
            <>
              <label>
                <span>{t(k("traceability.issuer"))}</span>
                <input
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                />
              </label>
              <label>
                <span>{t(k("traceability.issuedOn"))}</span>
                <input
                  type="date"
                  value={issuedOn}
                  onChange={(e) => setIssuedOn(e.target.value)}
                />
              </label>
              <label>
                <span>{t(k("traceability.validUntil"))}</span>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </label>
            </>
          )}
          {section === "requirements" && (
            <label>
              <span>{t(k("traceability.attributeCode"))}</span>
              <input
                value={extraCode}
                onChange={(e) => setExtraCode(e.target.value)}
              />
            </label>
          )}
          {(section === "requirements" ||
            section === "attributes" ||
            section === "certificates") && (
            <label>
              <span>
                {t(
                  k(
                    section === "requirements"
                      ? "traceability.attributeValue"
                      : section === "attributes"
                        ? "traceability.unit"
                        : "traceability.externalReference",
                  ),
                )}
              </span>
              <input
                value={extraValue}
                onChange={(e) => setExtraValue(e.target.value)}
              />
            </label>
          )}
          {(section === "policies" || section === "requirements") && (
            <label>
              <span>{t(k("traceability.shelfLifeDays"))}</span>
              <input
                type="number"
                min="0"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              />
            </label>
          )}
        </div>
      </form>
    </AppModal>
  );
}
